import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import '../../styles/theme-components.css';

const ParticipantForm = ({ questionnaireId }) => {
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    phone_number: '',
  });
  const [errors, setErrors] = useState({});
  const navigate = useNavigate();

  const fields = [
    { id: 'first-name', name: 'first_name', label: 'نام', type: 'text' },
    { id: 'last-name', name: 'last_name', label: 'نام خانوادگی', type: 'text' },
    {
      id: 'phone',
      name: 'phone_number',
      label: 'شماره واتساپ',
      type: 'tel',
      placeholder: 'مثال: +98 یا +7',
      pattern: '^\\+(98|7)\\d+$'
    },
  ];
  const normalizeDigits = (str) => {
    if (!str) return '';
    return str
      .replace(/[۰-۹]/g, (d) => String.fromCharCode(d.charCodeAt(0) - 1728)) // Persian digits
      .replace(/[٠-٩]/g, (d) => String.fromCharCode(d.charCodeAt(0) - 1632)); // Arabic digits
  };
  const handleChange = (e) => {
    const { name, value } = e.target;
    const normalizedValue = name === 'phone_number' ? normalizeDigits(value) : value;

    setFormData({ ...formData, [name]: normalizedValue });

    setErrors((prev) => ({
      ...prev,
      [name]: normalizedValue.trim() ? '' : prev[name],
    }));
  };

  const validateForm = () => {
    const newErrors = {};
    fields.forEach((field) => {
      if (!formData[field.name].trim()) {
        newErrors[field.name] = `${field.label} اجباری است`;
      }
      if (field.name === 'phone_number' && formData[field.name].trim()) {
        const phoneRegex = /^\+(98|7)\d+$/;
        if (!phoneRegex.test(formData[field.name])) {
          newErrors[field.name] = 'شماره واتس اپ باید با +98 یا +7 شروع شود';
        }
      }
    });
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    console.log('Debug: Form data:', formData);
    console.log('Debug: Questionnaire ID:', questionnaireId);

    if (!validateForm()) {
      toast.error('لطفا فیلدهای اجباری را تکمیل کنید.', {
        position: 'top-right',
        autoClose: 3000,
      });
      return;
    }

    if (!questionnaireId || isNaN(questionnaireId)) {
      console.error('Debug: Invalid or missing questionnaireId:', questionnaireId);
      toast.error('شناسه پرسشنامه نامعتبر است', {
        position: 'top-right',
        autoClose: 3000,
      });
      return;
    }

    const url = `https://contest.new-waay.com/survey/questionnaires/${questionnaireId}/start-session/`;

    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      console.log('Debug: Response status:', response.status);
      const contentType = response.headers.get('Content-Type');

      if (response.ok) {
        const data = await response.json();
        console.log('Debug: Response data:', data);
        const { access, refresh } = data;

        localStorage.setItem('access_token', access);
        localStorage.setItem('refresh_token', refresh);
        console.log('Stored access token:', localStorage.getItem('access_token'));
        console.log('Stored refresh token:', localStorage.getItem('refresh_token'));

        toast.success('جلسه با موفقیت شروع شد!', {
          position: 'top-right',
          autoClose: 3000,
        });
        navigate(`/questionnaire/${questionnaireId}/start`);
      } else {
        if (contentType && contentType.includes('application/json')) {
          const errorData = await response.json();
          console.error('Debug: Error response:', errorData);
          toast.error(`خطا: ${errorData.message || errorData.error || 'خطایی رخ داد'}`, {
            position: 'top-right',
            autoClose: 3000,
          });
        } else {
          console.error('Debug: Non-JSON error response:', await response.text());
          toast.error('خطای غیرمنتظره‌ای رخ داد.', {
            position: 'top-right',
            autoClose: 3000,
          });
        }
      }
    } catch (error) {
      console.error('Debug: Fetch error:', error);
      toast.error('اتصال به سرور ناموفق بود.', {
        position: 'top-right',
        autoClose: 3000,
      });
    }
  };

  return (
    <div className="theme-card p-4" style={{ direction: 'rtl' }}>
      <h2 className="text-lg font-semibold theme-text-primary mb-4">اطلاعات کاربری</h2>
      <form onSubmit={handleSubmit} className="space-y-4">
        {fields.map((field) => {
          const hasError = Boolean(errors[field.name]);

          return (
            <div key={field.id}>
              <label
                htmlFor={field.id}
                className="block text-xs font-medium theme-text-secondary mb-1"
              >
                {field.label}
              </label>

              <div className="relative">
                <input
                  type={field.type}
                  id={field.id}
                  name={field.name}
                  value={formData[field.name]}
                  onChange={handleChange}
                  placeholder={field.placeholder}
                  pattern={field.pattern}
                  className={`theme-input w-full px-3 py-2 rounded-lg text-sm 
                    ${hasError ? 'border-red-500 focus:border-red-500' : 'theme-input-border'}
                  `}
                  style={{ direction: 'rtl', textAlign: 'right' }}
                />

                {hasError && (
                  <div className="group cursor-pointer absolute left-2 top-1/2 -translate-y-1/2 flex items-center">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      strokeWidth="2"
                      stroke="currentColor"
                      className="w-4 h-4 text-red-500"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M11.25 3.375l-7.545 13.09a1.125 1.125 0 00.977 1.685h15.135a1.125 1.125 0 00.977-1.685l-7.545-13.09a1.125 1.125 0 00-1.926 0z"
                      />
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M12 9v4.5m.008 2.25h.008"
                      />
                    </svg>

                    <div
                      className="hidden group-hover:flex absolute top-full left-0 mt-1
                      bg-red-500 text-white text-xs font-normal px-2 py-1 rounded-md
                      shadow-lg max-w-xs"
                      style={{ whiteSpace: 'nowrap', direction: 'rtl' }}
                    >
                      {errors[field.name]}
                    </div>
                  </div>
                )}
              </div>
            </div>
          );
        })}

        <button
          type="submit"
          className="theme-button w-full mt-3 px-4 py-2 font-semibold rounded-lg text-sm"
        >
          شروع آزمون
        </button>
      </form>
    </div>
  );
};

export default ParticipantForm;